/**
 * SEO and Open Graph utilities for MiamSécur
 * Provides dynamic URL generation, image validation, and content sanitization
 */

/**
 * Retourne l'URL de base selon le domaine actuel
 * Supporte miamsecur.com et miamsecur.fr (avec et sans www)
 * Respecte EXACTEMENT le hostname utilisé sans transformation
 */
export const getBaseUrl = (): string => {
  // En SSR ou contexte serveur, fallback conservateur
  if (typeof window === 'undefined') {
    return 'https://www.miamsecur.com';
  }
  
  const { protocol, hostname } = window.location;
  
  // Respecter EXACTEMENT le hostname tel qu'utilisé
  // - miamsecur.com → https://miamsecur.com
  // - www.miamsecur.com → https://www.miamsecur.com
  // - miamsecur.fr → https://miamsecur.fr
  // - www.miamsecur.fr → https://www.miamsecur.fr
  return `${protocol}//${hostname}`;
};

/**
 * Retourne le domaine officiel sans protocole (pour twitter:domain)
 * Toujours www.miamsecur.com pour cohérence SEO
 */
export const getDomainName = (): string => {
  return 'www.miamsecur.com';
};

/**
 * Retourne l'URL complète pour une page donnée
 * Utilise TOUJOURS le domaine officiel pour les meta tags SEO
 */
export const getPageUrl = (path: string): string => {
  const baseUrl = getShareBaseUrl(); // Force www.miamsecur.com
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${normalizedPath}`;
};

/**
 * Retourne l'URL de l'image Open Graph officielle
 */
export const getOfficialOgImage = (): string => {
  return "https://storage.googleapis.com/gpt-engineer-file-uploads/LggAcg7gdFTNoI6sH0hWeYpWgYD3/social-images/social-1761862442454-Logo.jpg";
};

/**
 * Valide qu'une image est utilisable pour Open Graph
 * - Doit être HTTPS
 * - Format .jpg, .png, ou .webp
 * - Dimensions minimales recommandées : 1200x630
 * 
 * Note: La validation complète des dimensions nécessiterait un chargement
 * de l'image côté client, ce qui ralentirait le rendu. On se limite donc
 * à la validation de l'URL et du format.
 */
export const isValidOgImage = (url: string): boolean => {
  if (!url || typeof url !== 'string') return false;
  
  try {
    const parsed = new URL(url);
    
    // Doit être HTTPS
    if (parsed.protocol !== 'https:') return false;
    
    // Vérifier le format (basé sur l'extension)
    const validExtensions = ['.jpg', '.jpeg', '.png', '.webp'];
    const hasValidExtension = validExtensions.some(ext => 
      parsed.pathname.toLowerCase().endsWith(ext)
    );
    
    if (!hasValidExtension) return false;
    
    return true;
  } catch {
    return false;
  }
};

/**
 * Retourne la première image valide d'un tableau, ou l'image officielle
 */
export const getBestOgImage = (images: string[]): string => {
  const validImage = images.find(img => isValidOgImage(img));
  return validImage || getOfficialOgImage();
};

/**
 * Sanitize une chaîne pour utilisation dans les meta tags
 * Prévient l'injection HTML et limite la longueur
 */
export const sanitizeMetaContent = (content: string, maxLength: number = 160): string => {
  if (!content) return '';
  
  // Supprimer les balises HTML
  const stripped = content.replace(/<[^>]*>/g, '');
  
  // Échapper les caractères spéciaux
  const escaped = stripped
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
  
  // Limiter la longueur
  if (escaped.length > maxLength) {
    return escaped.substring(0, maxLength - 3) + '...';
  }
  
  return escaped;
};

/**
 * Retourne l'URL de base officielle pour le partage social
 * Toujours utiliser le domaine de production, jamais le preview
 */
export const getShareBaseUrl = (): string => {
  return 'https://www.miamsecur.com';
};

/**
 * Retourne l'URL complète pour le partage social
 * Utilise toujours le domaine officiel
 */
export const getShareUrl = (path: string): string => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${getShareBaseUrl()}${normalizedPath}`;
};
